import { motion } from "framer-motion";
import { fmtPrice } from "@/lib/format";

const W = 760, H = 340, PAD = { l: 16, r: 100, t: 20, b: 24 };

export const SignalChart = ({ signal }) => {
  const { chart, entryLevel, stop, targets, direction } = signal;
  const all = [...chart, entryLevel, stop, ...targets];
  const min = Math.min(...all), max = Math.max(...all);
  const pad = (max - min) * 0.08;
  const y = (v) => PAD.t + (1 - (v - (min - pad)) / (max - min + 2 * pad)) * (H - PAD.t - PAD.b);
  const x = (i) => PAD.l + (i / (chart.length - 1)) * (W - PAD.l - PAD.r);
  const d = chart.map((v, i) => `${i ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const area = `${d} L${x(chart.length - 1)},${H - PAD.b} L${x(0)},${H - PAD.b} Z`;
  const entryIdx = Math.floor(chart.length * 0.55);
  const levels = [
    { v: targets[1], label: "T2", cls: "pos" }, { v: targets[0], label: "T1", cls: "pos" },
    { v: entryLevel, label: "Entry", cls: "ink" }, { v: stop, label: "Stop", cls: "neg" },
  ];
  const color = { pos: "var(--pos)", neg: "var(--neg)", ink: "var(--cobalt)" };

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" data-testid="signal-chart" role="img" aria-label={`${signal.instrument} chart with entry, stop and targets`}>
      <defs>
        <linearGradient id="fill" x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor="var(--cobalt)" stopOpacity="0.10" /><stop offset="1" stopColor="var(--cobalt)" stopOpacity="0" /></linearGradient>
      </defs>
      {[0.25, 0.5, 0.75].map((p) => <line key={p} x1={PAD.l} x2={W - PAD.r} y1={PAD.t + p * (H - PAD.t - PAD.b)} y2={PAD.t + p * (H - PAD.t - PAD.b)} stroke="var(--line)" strokeDasharray="2 6" />)}
      {direction === "Long"
        ? <rect x={x(entryIdx)} width={W - PAD.r - x(entryIdx)} y={y(targets[0])} height={Math.max(0, y(entryLevel) - y(targets[0]))} fill="var(--pos)" opacity="0.06" />
        : <rect x={x(entryIdx)} width={W - PAD.r - x(entryIdx)} y={y(entryLevel)} height={Math.max(0, y(targets[0]) - y(entryLevel))} fill="var(--pos)" opacity="0.06" />}
      <rect x={x(entryIdx)} width={W - PAD.r - x(entryIdx)} y={Math.min(y(entryLevel), y(stop))} height={Math.abs(y(entryLevel) - y(stop))} fill="var(--neg)" opacity="0.05" />
      <motion.path d={area} fill="url(#fill)" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.8, duration: 0.8 }} />
      <motion.path d={d} fill="none" stroke="var(--ink)" strokeWidth="1.5" strokeLinejoin="round" initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 1.6, ease: "easeInOut" }} />
      {levels.map((l, i) => (
        <motion.g key={l.label} initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 1 + i * 0.12, duration: 0.5 }}>
          <line x1={PAD.l} x2={W - PAD.r} y1={y(l.v)} y2={y(l.v)} stroke={color[l.cls]} strokeWidth="1" strokeDasharray={l.cls === "ink" ? "0" : "4 4"} opacity="0.8" />
          <rect x={W - PAD.r + 6} y={y(l.v) - 9} width={PAD.r - 10} height="18" rx="4" fill={color[l.cls]} opacity="0.1" />
          <text x={W - PAD.r + 10} y={y(l.v) + 4} fontFamily="IBM Plex Mono" fontSize="10" fill={color[l.cls]}>{l.label} {fmtPrice(l.v)}</text>
        </motion.g>
      ))}
      <motion.circle cx={x(entryIdx)} cy={y(entryLevel)} r="4" fill="var(--cobalt)" initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 1.4 }} />
      <circle cx={x(chart.length - 1)} cy={y(chart[chart.length - 1])} r="3" fill="var(--ink)" />
    </svg>
  );
};
